import { useFormik } from "formik";
import { noop } from "lodash";
import { useEffect, useRef } from "react";
import { connect, type ConnectedProps } from "react-redux";

import Restricted from "../../../Application/Restricted";
import SettingsForm from "./SettingsForm/SettingsForm";

import { RolePermissions } from "../../../../enums";
import { bindAction, type IWizardStep } from "../../../../interfaces";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { saveSettings } from "../state/slices/DetailsSlice";
import { normalizedTagsSelector } from "../state/selectors";
import { updateTagsAction } from "../state/thunks/EntityStateThunk";
import { fetchTagsAction } from "../state/thunks/TagsThunk";
import { settingsValidationSchema } from "../../../../utils/validationSchemas/scormValidationSchema";

export type SettingsTabProps = IWizardStep & PropsFromRedux;

export const SettingsTab = (props: SettingsTabProps) => {
  const { fetchTags, updateTags, settings, normalizedTags, isLoading, acceptHandlers, entityId, disabled } = props;

  useEffect(() => {
    acceptHandlers?.({
      onPrevious: saveTags,
      onNext: saveTags,
    });
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tagsFormik = useFormik({
    initialValues: settings,
    validationSchema: settingsValidationSchema,
    onSubmit: noop,
    enableReinitialize: true,
  });

  const valuesRef = useRef(tagsFormik.values);
  useEffect(() => {
    valuesRef.current = tagsFormik.values;
  }, [tagsFormik.values]);

  const saveTags = () => {
    props.saveSettings(valuesRef.current);
  };

  return (
    <Restricted
      permissions={permissions}
      renderContent={(hasPermission) => (
        <SettingsForm
          entityId={entityId}
          tagsFormik={tagsFormik}
          normalizedTags={normalizedTags}
          updateTags={updateTags}
          settings={settings}
          isDisabled={disabled || !hasPermission}
          isLoading={isLoading}
        />
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const details = state.library.scorm.details;
  return {
    entityId: state.library.scorm.entityState.entityId,
    settings: details.item.settings,
    disabled: details.item.isPurchased,
    isLoading: details.isLoading,
    normalizedTags: normalizedTagsSelector(state),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchTags: bindAction(fetchTagsAction, dispatch),
  saveSettings: bindAction(saveSettings, dispatch),
  updateTags: bindAction(updateTagsAction, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const permissions = [RolePermissions.AssetsCreate];

const ConnectedComponent = connector(SettingsTab);
export default ConnectedComponent;
